import { IconsList } from '@mint-lib/icons/allIcons';
import { iconVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { SvgIconProps } from '@mui/material/SvgIcon';

import {
  IconLoadingState,
  useLazyLoadedIcon,
} from '../../hooks/useLazyLoadedIcon.js';
import Box from '../Box/Box.jsx';

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    purple: true;
    teal: true;
    text: true;
    tertiary: true;
    yellow: true;
    dark: true;
  }
}

// @ts-ignore
export interface IconProps extends SvgIconProps {
  /**
   * Hang over the name of icon to implement icon component
   */

  component: IconsList;

  /**
   * Override or extend the styles applied to the component.
   */

  classes?: SvgIconProps['classes'];

  /**
   * The color of the component.
   * It supports those theme colors that make sense for this component.
   * You can use the htmlColor prop to apply a color attribute to the SVG element.
   */

  color?: SvgIconProps['color'];

  /**
   * The fontSize applied to the icon. Defaults to 16px,
   * but can be configure to inherit font size.
   * @default medium
   */

  fontSize?: SvgIconProps['fontSize'];

  /**
   * Applies a color attribute to the SVG element.
   */

  htmlColor?: SvgIconProps['htmlColor'];
}

/**
 * @deprecated
 * The component is deprecated.
 * Use `@mint-lib/icons` library instead of this. All new icons will be available only in `@mint-lib/icons`.
 * Please, replace the import when you have a spare time.
 *
 * @example
 * ```tsx
 * import { NameOfIcon } from "@mint-lib/icons";
 *
 * function App() {
 *   return (
 *     <NameOfIcon className="fill-icon-primary ....." />
 *   );
 * }
 *```
 */
const Icon = ({
  component,
  fontSize = 'medium',
  color = 'inherit',
  className,
  ...props
}: IconProps) => {
  const { SvgIcon, state } = useLazyLoadedIcon(component);
  const tailwindClasses = iconVariants({
    size: fontSize,
    color,
    className,
  });

  return (
    <Box
      className={cn(tailwindClasses, 'MuiSvgIcon-root')}
      component={SvgIcon}
      role="img"
      // @ts-ignore this is a wrong attribute but findIcon testing util uses it
      alt={state === IconLoadingState.Loaded ? component : ''}
      {...props}
      aria-hidden={state !== IconLoadingState.Loaded}
      aria-label={
        state === IconLoadingState.Loaded ? props['aria-label'] : undefined
      }
    />
  );
};

export default Icon;
